import {
  ApiResponse,
  IDonViGeojsonFeature,
  IDonViGeojsonPreviewPayload,
  IDonViGeojsonPreviewResponse,
  IDonViResponseBase,
  IKetoanDonViPreview,
  IKetoanDonViPreviewResponse,
  IKetoanSyncPayload,
  IKetoanSyncResult,
} from '@/core/interfaces/ApiResponses';
import { apiServiceInstance } from '@/core/helpers/utils';

function normalizePreviewPayload(
  body: ApiResponse<IKetoanDonViPreviewResponse>
): { items: IKetoanDonViPreview[]; message?: string; count: number } {
  const message = body.message;
  const payload = body.data;
  const items = Array.isArray(payload?.new_don_vi) ? payload.new_don_vi : [];
  const count =
    typeof payload?.count === 'number' ? payload.count : items.length;

  return { items, message, count };
}

function normalizeGeojsonFeature(
  payload: IDonViGeojsonPreviewResponse | IDonViGeojsonFeature | null | undefined
): IDonViGeojsonFeature | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  if (payload.type === 'Feature' && 'properties' in payload) {
    return payload as IDonViGeojsonFeature;
  }

  if ('features' in payload && Array.isArray(payload.features)) {
    const [firstFeature] = payload.features;
    if (firstFeature) {
      return firstFeature;
    }
  }

  if ('properties' in payload && payload.properties) {
    return {
      type: 'Feature',
      geometry: 'geometry' in payload ? payload.geometry ?? null : null,
      properties: payload.properties,
    };
  }

  return null;
}

export class DonViService {
  static fetchCurrentDepartmentById(departmentId: number): Promise<{
    data: ApiResponse<IDonViResponseBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/don-vi/chi-tiet/${departmentId}`
    );
  }

  static fetchAllDepartments(): Promise<{
    data: ApiResponse<IDonViResponseBase[]>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      '/api/v1/don-vi/danh-sach'
    );
  }

  static fetchDepartmentsByCapDonVi(capDonVi: number): Promise<{
    data: ApiResponse<IDonViResponseBase[]>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      '/api/v1/don-vi/danh-sach',
      { params: { cap_don_vi: capDonVi } }
    );
  }

  static async fetchNewDonViFromKetoan(params?: {
    maDonVi?: string;
  }): Promise<{ items: IKetoanDonViPreview[]; message?: string; count: number }> {
    const response = await apiServiceInstance().vueInstance.axios.get<
      ApiResponse<IKetoanDonViPreviewResponse>
    >('/api/v1/don-vi/ketoan/moi', {
      params: {
        ...(params?.maDonVi?.trim()
          ? { maDonVi: params.maDonVi.trim() }
          : {}),
      },
    });

    return normalizePreviewPayload(response.data);
  }

  static async previewDonViGeojson(
    payload: IDonViGeojsonPreviewPayload
  ): Promise<IDonViGeojsonFeature | null> {
    const response = await apiServiceInstance().vueInstance.axios.post<
      ApiResponse<IDonViGeojsonPreviewResponse | IDonViGeojsonFeature>
    >('/api/v1/don-vi/geojson/preview', payload);

    return normalizeGeojsonFeature(response.data?.data);
  }

  static syncDonViFromKetoan(payload: IKetoanSyncPayload): Promise<{
    data: ApiResponse<IKetoanSyncResult>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      '/api/v1/don-vi/ketoan/dong-bo',
      payload
    );
  }
}
